import { useMemo, useState } from "react";
import EditModal from "../ui/EditModal";
import { formatDate } from "../../utils/dateUtils";
import LoggedByChip from "../ui/LoggedByChip";

const BreastfeedingTable = ({ data, onUpdate, onDelete }) => {
  const [page, setPage]           = useState(1);
  const [rowsPerPage]             = useState(5);
  const [sortDir, setSortDir]     = useState("desc");
  const [editingItem, setEditingItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving]   = useState(false);

  const sorted = useMemo(() =>
    data.slice().sort((a, b) => {
      if (a.date < b.date) return sortDir === "asc" ? -1 : 1;
      if (a.date > b.date) return sortDir === "asc" ? 1 : -1;
      return 0;
    }),
    [data, sortDir]
  );

  const pageCount = Math.ceil(sorted.length / rowsPerPage);
  const pageData  = sorted.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const today = new Date();
  const todayFeeds = data.filter((item) => {
    const d = new Date(item.date);
    return d.toDateString() === today.toDateString();
  });
  const todayMinutes = todayFeeds.reduce((s, i) => s + parseFloat(i.duration || 0), 0);

  const handleEdit = (item) => { setEditingItem(item); setIsModalOpen(true); };
  const handleSave = async (formData) => {
    setIsSaving(true);
    try {
      await onUpdate(editingItem.id, formData);
      setIsModalOpen(false);
      setEditingItem(null);
    } catch { alert("Failed to save changes"); }
    finally { setIsSaving(false); }
  };
  const handleDelete = async (id) => {
    if (window.confirm("Delete this entry?")) {
      try { await onDelete(id); }
      catch { alert("Failed to delete entry"); }
    }
  };

  const sideBadge = (side) => {
    const map = { left: "info", right: "warning", both: "success" };
    return <span className={`badge bg-${map[side] || "secondary"}`}>{side}</span>;
  };

  const editFields = [
    { name: "date",     label: "Date & Time",    type: "datetime-local", required: true },
    { name: "side",     label: "Side",           type: "select", required: true,
      options: [{ label: "Left", value: "left" }, { label: "Right", value: "right" }, { label: "Both", value: "both" }] },
    { name: "duration", label: "Duration (min)", type: "number", step: "1", required: true },
    { name: "notes",    label: "Notes",          type: "text" },
  ];

  return (
    <>
      {todayMinutes > 0 && (
        <div className="alert alert-info mb-3">
          <strong>Today:</strong> {todayFeeds.length} session{todayFeeds.length !== 1 ? "s" : ""} · {todayMinutes} min total
        </div>
      )}

      <div className="table-responsive">
        <table className="table table-sm table-striped table-hover">
          <thead className="table-light">
            <tr>
              <th style={{ cursor: "pointer" }} onClick={() => { setSortDir(d => d === "asc" ? "desc" : "asc"); setPage(1); }}>
                Date {sortDir === "desc" ? "▼" : "▲"}
              </th>
              <th>Side</th>
              <th>Duration</th>
              <th>Notes</th>
              <th>Logged by</th>
              <th style={{ width: "80px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageData.map((row) => (
              <tr key={row.id}>
                <td>{formatDate(row.date)}</td>
                <td>{sideBadge(row.side)}</td>
                <td>{row.duration} min</td>
                <td>{row.notes || "—"}</td>
                <td><LoggedByChip loggedBy={row.loggedBy} /></td>
                <td>
                  <button className="btn btn-xs btn-warning me-1" onClick={() => handleEdit(row)}
                    style={{ padding: "0.25rem 0.5rem", fontSize: "0.875rem" }}>✎</button>
                  <button className="btn btn-xs btn-danger" onClick={() => handleDelete(row.id)}
                    style={{ padding: "0.25rem 0.5rem", fontSize: "0.875rem" }}>✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <nav>
          <ul className="pagination pagination-sm">
            <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => setPage(p => Math.max(p - 1, 1))}>Prev</button>
            </li>
            {[...Array(pageCount)].map((_, i) => (
              <li key={i + 1} className={`page-item ${page === i + 1 ? "active" : ""}`}>
                <button className="page-link" onClick={() => setPage(i + 1)}>{i + 1}</button>
              </li>
            ))}
            <li className={`page-item ${page === pageCount ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => setPage(p => Math.min(p + 1, pageCount))}>Next</button>
            </li>
          </ul>
        </nav>
      )}

      <EditModal
        isOpen={isModalOpen}
        item={editingItem}
        title="Edit Breastfeeding Session"
        fields={editFields}
        onSave={handleSave}
        onCancel={() => { setIsModalOpen(false); setEditingItem(null); }}
        isLoading={isSaving}
      />
    </>
  );
};

export default BreastfeedingTable;
